import AsyncCacheManager from './AsyncCacheManager';
import { getCache } from './modules/registry';

export type CacheSnapshot = Awaited<ReturnType<ReturnType<typeof getCache>['exportSnapshot']>>;

/**
 * Clear the ENTIRE app cache across all namespaces and groups.
 */
export async function clearEntireCache(): Promise<void> {
  await AsyncCacheManager.clearAll();
}

/**
 * Clear all cache entries for a specific namespace+group scope.
 * If you pass only group or only namespace, it will clear entries matching that prefix
 * according to how keys are composed (ns:group:key). Prefer passing both.
 */
export async function clearScope(scope: { namespace?: string; group?: string; key?: string } = {}): Promise<void> {
  const { namespace, group, key } = scope;
  const cache = getCache({ namespace, group });
  await cache.clear(key);
}

/**
 * Clear all cache entries for a given group ACROSS ALL namespaces.
 * This scans the key-value store and removes any entries whose second segment equals the group
 * (keys are composed as "namespace:group:..."), and also handles entries without a namespace
 * where the first segment equals the group.
 */
export async function clearGroupAcrossNamespaces(group: string): Promise<void> {
  if (!group || !group.trim()) return;
  const db = await AsyncCacheManager.openSharedDb();

  // Collect keys to delete first to keep transactions short
  const keysToDelete: string[] = await new Promise((resolve, reject) => {
    const out: string[] = [];
    const tx = db.transaction('kv', 'readonly');
    const store = tx.objectStore('kv');
    const req = store.openKeyCursor();
    req.onsuccess = () => {
      const cursor = req.result as IDBCursor | null;
      if (cursor) {
        const k = String(cursor.key);
        const parts = k.split(':');
        // match ns:group:... or group:...
        if ((parts.length >= 2 && parts[1] === group) || (parts.length >= 1 && parts[0] === group)) {
          out.push(k);
        }
        cursor.continue();
      } else {
        resolve(out);
      }
    };
    req.onerror = () => resolve([]);
  });

  // Delete from main and meta stores in small per-key transactions
  for (const k of keysToDelete) {
    await new Promise<void>((resolve) => {
      const tx1 = db.transaction('kv', 'readwrite');
      const s1 = tx1.objectStore('kv');
      const r1 = s1.delete(k);
      r1.onsuccess = () => resolve();
      r1.onerror = () => resolve();
    });
    await new Promise<void>((resolve) => {
      const tx2 = db.transaction('meta', 'readwrite');
      const s2 = tx2.objectStore('meta');
      const r2 = s2.delete(k);
      r2.onsuccess = () => resolve();
      r2.onerror = () => resolve();
    });
  }
}

/**
 * Get a snapshot (metrics + values) for a specific namespace+group scope.
 */
export async function getScopeSnapshot(namespace?: string, group?: string): Promise<CacheSnapshot> {
  const cache = getCache({ namespace, group });
  return cache.exportSnapshot();
}
